<template>
  <div class="table-container">
    <!-- 顶部工具栏：全选 + 已选计数 + 反选按钮 -->
    <div class="toolbar">
      <label class="checkbox-label">
        <input type="checkbox" v-model="allSelected" />
        <span>全选</span>
      </label>
      <span class="selected-info">已选 {{ selectedCount }} 项</span>
      <button class="inverse-btn" @click="inverseSelection">反选</button>
    </div>

    <!-- 表格主体 -->
    <div class="table-responsive">
      <table>
        <thead>
          <tr>
            <th class="checkbox-col"></th>
            <th>序号</th>
            <th>模块名称</th>
            <th>功能</th>
            <th>使用天数</th>
            <th>数量</th>
            <th>价格(元)</th>
          </tr>
        </thead>
      <tbody>
  <tr
    v-for="(row, index) in rows"
    :key="row.id"
    :class="{ 'child-row': row.type === 'sku' }"
  >
    <td class="checkbox-col">
      <!-- 只有商品无子项 或 子项才显示复选框 -->
      <input
        v-if="!(row.type === 'product' && row.hasChildren)"
        type="checkbox"
        v-model="selectedIds"
        :value="row.id"
      />
    </td>
    <td>{{ row.sn }}</td>
    <td>{{ row.moduleName }}</td>
    <td>{{ row.function }}</td>
    <td>{{ row.days }}</td>
    <td>{{ row.quantity }}</td>
    <td>￥{{ row.price.toLocaleString() }}</td>
  </tr>
</tbody>
      </table>
    </div>

    <!-- 底部总价与操作按钮 -->
    <div class="footer">
      <div class="total">总价 ￥{{ totalPrice.toLocaleString() }}</div>
      <div class="trial">*注意首次申请试用期为14天</div>
      <div class="actions">
        <button class="btn-outline">申请试用</button>
        <button class="btn-primary">结算</button>
      </div>
    </div>
    <div class="copyright">微科斯公司ERP管理系统 2025-2026 深圳市微科斯科技有限公司</div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

// 原始数据（从 API 获取）
const products = ref([
  {
    id: "8592588ea3cbec32ab71fc69d061a2b7",
    productName: "基础资料",
    price: 15000,
    stockQty: 0,
    // 其他字段省略...
  },
  {
    id: "858ef6a67ed82eef7a9666e8701992ec",
    productName: "出库管理",
    price: 0,
    stockQty: 0,
    productsGetSkuDto: {
      productsSkuInfoDto: [
        { skuId: "859204c7d144823f499f8cdff142cd89", productsSpecsValueId: ["出库扫码"], price: 11, stockQty: 111 },
        { skuId: "859204c88b85f1d0a51d9b90689b962e", productsSpecsValueId: ["复检"], price: 11, stockQty: 111 },
        { skuId: "859204c89345c403d8fe20007f6d9adc", productsSpecsValueId: ["打版"], price: 11, stockQty: 111 },
        { skuId: "859204c8983c2f622ae21a28638c37ca", productsSpecsValueId: ["装箱"], price: 11, stockQty: 111 },
        { skuId: "859204c89d341ba065c6ccec408fd79c", productsSpecsValueId: ["称重"], price: 11, stockQty: 111 }
      ]
    }
  }
]);

// 展平成行数据
const rows = computed(() => {
  const flat = [];
  let mainSn = 1; // 商品主序号

  products.value.forEach((product) => {
    // 商品本身行
    flat.push({
      id: `p-${product.id}`,
      type: 'product',
      sn: mainSn, // 主序号
      moduleName: product.productName,
      function: '',
      days: product.productName === '基础资料' ? 180 : 0,
      quantity: product.stockQty || 0,
      price: product.price || 0
    });

    // 子行（SKU）
    const skus = product.productsGetSkuDto?.productsSkuInfoDto || [];
    let subSn = 1;
    skus.forEach(sku => {
      flat.push({
        id: `s-${sku.skuId}`,
        type: 'sku',
        sn: `${mainSn}.${subSn}`, // 层级序号
        moduleName: product.productName,
        function: sku.productsSpecsValueId?.join(' / ') || '',
        days: 111,
        quantity: sku.stockQty || 0,
        price: sku.price || 0
      });
      subSn++;
    });

    mainSn++; // 处理下一个商品
  });
  return flat;
});

// 所有可选行的 id 列表
const allIds = computed(() => rows.value.map(row => row.id));

// 当前选中的 id 列表
const selectedIds = ref([]);

// 全选计算属性
const allSelected = computed({
  get: () => selectedIds.value.length === allIds.value.length,
  set: (value) => {
    if (value) {
      selectedIds.value = [...allIds.value]; // 全选
    } else {
      selectedIds.value = []; // 全不选
    }
  }
});

// 已选数量
const selectedCount = computed(() => selectedIds.value.length);

// 总价计算（选中项的价格 × 数量之和）
const totalPrice = computed(() => {
  let total = 0;
  rows.value.forEach(row => {
    if (selectedIds.value.includes(row.id)) {
      total += row.price * row.quantity;
    }
  });
  return total;
});

// 反选：将当前选中状态取反
const inverseSelection = () => {
  const all = allIds.value;
  const currentlySelected = selectedIds.value;
  const newSelected = all.filter(id => !currentlySelected.includes(id));
  selectedIds.value = newSelected;
};
</script>

<style scoped>
/* 样式与之前保持一致 */
.table-container {
  background: white;
  border-radius: 20px;
  padding: 20px;
  border: 1px solid #d6e5ff;
  box-shadow: 0 4px 12px rgba(0, 51, 102, 0.05);
  max-width: 1000px;
  margin: 0 auto;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0ecff;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #1e4870;
}

.checkbox-label input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.selected-info {
  font-size: 0.9rem;
  color: #0066cc;
  background: #e2f0ff;
  padding: 4px 12px;
  border-radius: 30px;
}

.inverse-btn {
  padding: 4px 12px;
  background: #e6f0ff;
  border: 1px solid #b9d3ff;
  border-radius: 30px;
  color: #0066cc;
  font-size: 0.85rem;
  cursor: pointer;
  transition: 0.2s;
}

.inverse-btn:hover {
  background: #0066cc;
  color: white;
}

.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

th {
  background: #e2eeff;
  padding: 14px 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #00356b;
  text-align: center;
  border-bottom: 2px solid #99bdff;
}

td {
  padding: 12px 8px;
  border-bottom: 1px solid #dae6ff;
  text-align: center;
  font-size: 0.9rem;
  color: #1e3d63;
}

.checkbox-col {
  width: 40px;
  text-align: center;
}

.checkbox-col input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

/* 子行缩进（SKU行） */
.child-row td:first-of-type {
  padding-left: 40px;
}

.footer {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e0ecff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.total {
  font-size: 1.2rem;
  font-weight: 600;
  color: #f56c6c;
}

.trial {
  color: #003f80;
  font-size: 0.9rem;
}

.actions {
  display: flex;
  gap: 12px;
}

.btn-outline,
.btn-primary {
  padding: 10px 24px;
  border: none;
  border-radius: 30px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: 0.2s;
}

.btn-outline {
  background: white;
  border: 2px solid #0066cc;
  color: #0066cc;
}

.btn-outline:hover {
  background: #e2f0ff;
}

.btn-primary {
  background: #0066cc;
  color: white;
}

.btn-primary:hover {
  background: #004c99;
}

.copyright {
  margin-top: 20px;
  text-align: center;
  color: #6f8db0;
  font-size: 0.8rem;
}

@media (max-width: 768px) {
  .toolbar {
    flex-wrap: wrap;
  }
  .footer {
    flex-direction: column;
    align-items: stretch;
  }
  .actions {
    flex-direction: column;
  }
  .btn-outline,
  .btn-primary {
    width: 100%;
  }
}
</style>